import { memo } from 'react';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import Cancel from '@mui/icons-material/Cancel';
import Delete from '@mui/icons-material/Delete';
import ShoppingCart from '@mui/icons-material/ShoppingCart';

export const ItemsCart = memo(({ open, isPatroller, onClose, items, onCheckout, onRemoveItem }) => {
    // Filter out paid items from cart display - they can be selected for copying but not for payment
    const cartItems = items.filter(item => !item.feePaid);
    const ITEM_FEE = 2.00;
    const subtotal = cartItems.reduce((sum, item) => sum + ((item.quantity || 1) * ITEM_FEE), 0);
    const discount = isPatroller ? subtotal : 0;
    const donations = items.filter(item => item.donation).length * ITEM_FEE;
    const total = Math.max(subtotal - discount - donations, 0);

    return (
        <Drawer
            anchor="right"
            open={ open }
            onClose={ onClose }
            sx={ { width: 350 } }
        >
            <div style={ { width: 350, padding: '16px', display: 'flex', flexDirection: 'column', height: '100%' } }>
                <div style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' } }>
                    <Typography variant="h6">Cart</Typography>

                    <IconButton onClick={ onClose }>
                        <Cancel />
                    </IconButton>
                </div>

                <div style={ { flex: 1, overflowY: 'auto' } }>
                    { cartItems.length === 0 ? (
                        <Typography variant="body2">No items in cart.</Typography>
                    ) : (
                        cartItems.map((item, index) => (
                            <div key={ item.id || index } style={ { display: 'flex', alignItems: 'center', marginBottom: '16px', padding: '8px', border: '1px solid #ddd', borderRadius: '8px' } }>
                                <Avatar
                                    src={ item.imageUrl }
                                    alt={ item.name || `Item ${ index + 1 }` }
                                    sx={ { width: 50, height: 50, marginRight: '12px' } }
                                >
                                    { item.name ? item.name.charAt(0).toUpperCase() : 'I' }
                                </Avatar>

                                <div style={ { flex: 1 } }>
                                    <Typography variant="body1" sx={ { fontWeight: 'bold' } }>{ item.name || `Item ${ index + 1 }` }</Typography>
                                    <Typography variant="body2" color="text.secondary">${ ((item.quantity || 1) * ITEM_FEE).toFixed(2) }</Typography>
                                </div>

                                <IconButton
                                    onClick={ () => onRemoveItem(item) }
                                    color="error"
                                    disabled={ item.feePaid }
                                    title={ item.feePaid ? "Cannot remove paid items from cart" : "Remove from cart" }
                                >
                                    <Delete />
                                </IconButton>
                            </div>
                        ))
                    ) }
                </div>

                { cartItems.length > 0 && (
                    <div style={ { paddingTop: '16px' } }>
                        <div style={ { display: 'flex', justifyContent: 'space-between', marginBottom: '16px', borderTop: '1px solid #ddd', paddingTop: '8px' } }>
                            <Typography variant="h6">Subtotal:</Typography>
                            <Typography variant="h6">${ subtotal.toFixed(2) }</Typography>
                        </div>

                        { (isPatroller || donations > 0) && (
                            <div style={ { display: 'flex', justifyContent: 'space-between', marginBottom: '16px', borderTop: '1px solid #ddd', paddingTop: '8px' } }>
                                <Typography variant="h6">{ isPatroller ? 'Patroller' : 'Donation' } Discount:</Typography>
                                <Typography variant="h6" color='success' >-${ subtotal.toFixed(2) }</Typography>
                            </div>
                        ) }

                        <div style={ { display: 'flex', justifyContent: 'space-between', marginBottom: '16px', borderTop: '1px solid #ddd', paddingTop: '8px' } }>
                            <Typography variant="h6">Total:</Typography>
                            <Typography variant="h6">${ total.toFixed(2) }</Typography>
                        </div>

                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={ onCheckout }
                            startIcon={ <ShoppingCart /> }
                        >
                            Checkout
                        </Button>
                    </div>
                ) }
            </div>
        </Drawer>
    );
});

ItemsCart.displayName = 'Cart Sidebar';